// @docs:
//   - docs/core/operations/WEATHER_ADMIN_DASHBOARD.md
//
// Admin › Weather — the Vortex weather engine at a glance, from what already
// exists: the CDN catalog, an R2 listing, and the vortex-weather worker's own
// log lines (every cron kick records what the ingest container answered).
// Manual triggers go through the API, which holds the trigger secret.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon, ChevronDownIcon, ChevronUpDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { formatLocal, ingestTime, runIdToMs, sortRows, type SortDir } from './weatherFormat';
import { WeatherTilesPanel } from './WeatherTilesPanel';
import {
  api,
  type WeatherAvailabilityResult,
  type WeatherCatalogModel,
  type WeatherOverview,
  type WeatherRunSummary,
  type WeatherTilesResult,
  type WeatherTriggerParams,
} from '../../services/api';

const POLL_MS = 60_000;

/** The models the engine ingests: trigger key, catalog id, and what the ingest is set to produce. */
const SOURCES = [
  { key: 'ecmwf', catalogId: 'ecmwf0p25', name: 'ECMWF IFS 0.25°', horizon: '0–360 h', cycles: ['00', '12'] },
  { key: 'gfs', catalogId: 'gfs025', name: 'NOAA GFS 0.25°', horizon: '0–240 h', cycles: ['00', '06', '12', '18'] },
  { key: 'icon', catalogId: 'icon025', name: 'DWD ICON 0.25°', horizon: '0–180 h', cycles: ['00', '12'] },
] as const;
type SourceKey = (typeof SOURCES)[number]['key'];

/** Times are shown in the viewer's own time zone, with the zone named. */
const local = (ts: number) => formatLocal(ts);
const hoursAgo = (ts: number | null | undefined, now: number) => (ts ? `${Math.max(0, Math.round((now - ts) / 3_600_000))} h` : '—');

const TONE = {
  ok: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  warn: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  err: 'bg-red-500/10 text-red-300 border-red-500/30',
  info: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/30',
  mute: 'bg-slate-500/15 text-slate-300 border-slate-500/40',
} as const;
type Tone = keyof typeof TONE;

function Pill({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[13px] font-semibold border whitespace-nowrap ${TONE[tone]}`}>{children}</span>;
}

function Card({ title, caption, children }: { title: string; caption?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-slate-700 bg-slate-800/50">
      <div className="px-5 py-4 border-b border-slate-700 flex items-baseline justify-between gap-4 flex-wrap">
        <h2 className="text-[17px] font-bold text-white">{title}</h2>
        {caption && <p className="text-[13px] text-slate-400">{caption}</p>}
      </div>
      {children}
    </section>
  );
}

/** A column header that sorts its table: click to sort, click again to reverse. */
function SortHeader({ label, active, dir, onSort }: { label: string; active: boolean; dir: SortDir; onSort: () => void }) {
  const Icon = !active ? ChevronUpDownIcon : dir === 'asc' ? ChevronUpIcon : ChevronDownIcon;
  return (
    <th scope="col" aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'} className="px-5 py-1 text-left font-semibold">
      <button type="button" onClick={onSort} className={`min-h-[44px] inline-flex items-center gap-1 text-[13px] font-semibold hover:text-white ${active ? 'text-slate-100' : 'text-slate-400'}`}>
        {label}
        <Icon className="h-4 w-4" aria-hidden="true" />
      </button>
    </th>
  );
}

function useSort<K extends string>(initial: K | null, initialDir: SortDir = 'asc') {
  const [key, setKey] = useState<K | null>(initial);
  const [dir, setDir] = useState<SortDir>(initialDir);
  const toggle = (k: K) => {
    if (k === key) setDir(dir === 'asc' ? 'desc' : 'asc');
    else { setKey(k); setDir('asc'); }
  };
  return { key, dir, toggle };
}

const OUTCOME: Record<WeatherRunSummary['outcome'], [Tone, string]> = {
  done: ['ok', 'Done'],
  running: ['info', 'Running'],
  queued: ['mute', 'Queued'],
  unfinished: ['err', 'Replaced unfinished'],
};
const STATUS_ORDER = ['queued', 'started', 'in_progress', 'skipped'];
const STATUS_BAR: Record<string, string> = { queued: 'bg-slate-400', started: 'bg-cyan-400', in_progress: 'bg-amber-400', skipped: 'bg-emerald-400' };

type SourceRow = { name: string; status: [Tone, string]; cat: WeatherCatalogModel | null; last: { ts: number } | null };
type SourceSortKey = 'name' | 'status' | 'run' | 'published' | 'steps' | 'regions' | 'products' | 'last';
const SOURCE_COLUMNS: [SourceSortKey, string][] = [['name', 'Source'], ['status', 'Status'], ['run', 'Catalog run'], ['published', 'Published'], ['steps', 'Steps'], ['regions', 'Regions'], ['products', 'Products'], ['last', 'Last kick']];
const SOURCE_SORT: Record<SourceSortKey, (s: SourceRow) => string | number | null> = {
  name: (s) => s.name,
  status: (s) => s.status[1],
  run: (s) => s.cat?.runId ?? null,
  published: (s) => (s.cat?.publishedAt ? Date.parse(s.cat.publishedAt) : null),
  steps: (s) => s.cat?.hours.length ?? null,
  regions: (s) => s.cat?.regions.length ?? null,
  products: (s) => s.cat?.products.length ?? null,
  last: (s) => s.last?.ts ?? null,
};
type RunSortKey = 'model' | 'run' | 'answers' | 'kicks' | 'time' | 'outcome';
const RUN_COLUMNS: [RunSortKey, string][] = [['model', 'Model'], ['run', 'Run'], ['answers', 'What the container answered'], ['kicks', 'First → last kick'], ['time', 'Ingest time'], ['outcome', 'Outcome']];
const OUTCOME_ORDER: Record<WeatherRunSummary['outcome'], number> = { running: 0, queued: 1, unfinished: 2, done: 3 };
const RUN_SORT: Record<RunSortKey, (r: WeatherRunSummary) => string | number | null> = {
  model: (r) => r.model,
  run: (r) => runIdToMs(r.runId),
  answers: (r) => r.kicks,
  kicks: (r) => r.lastTs,
  time: (r) => ingestTime(r.attempts, Date.now())?.secs ?? null,
  outcome: (r) => OUTCOME_ORDER[r.outcome],
};

export function AdminWeatherPage() {
  const [data, setData] = useState<WeatherOverview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tiles, setTiles] = useState<WeatherTilesResult | null>(null);
  const [tilesError, setTilesError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const tilesLoad = api.adminWeatherTiles()
      .then((t) => { setTiles(t); setTilesError(null); })
      .catch(() => setTilesError('Could not read the worldwide tile coverage. The rest of the page is current.'));
    try {
      setData(await api.adminWeatherOverview(24));
      setLoadError(null);
    } catch {
      setLoadError('Could not load the weather overview.');
    } finally {
      await tilesLoad;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => { void load(); }, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const now = data?.generatedAt ?? Date.now();
  const catalogModels: WeatherCatalogModel[] = data?.catalog.ok ? data.catalog.models : [];
  const logs = data?.logs;
  const logSummary = logs && logs.configured && !('error' in logs && logs.error) && 'runs' in logs ? logs : null;
  const stalled = useMemo(() => new Set(logSummary?.stalled.map((s) => s.model) ?? []), [logSummary]);
  const sourceSort = useSort<SourceSortKey>(null);
  const runSort = useSort<RunSortKey>(null);

  const sourceRows = SOURCES.map((s) => {
    const cat = catalogModels.find((m) => m.id === s.catalogId) ?? null;
    const last = logSummary?.lastKick[s.key] ?? null;
    let status: [Tone, string];
    if (stalled.has(s.key)) status = ['err', 'Falling behind'];
    else if (cat) status = ['ok', 'Live'];
    else if (last) status = ['warn', `Ingesting · not in catalog`];
    else status = ['mute', 'Not in catalog'];
    return { ...s, cat, last, status };
  });
  const sortedSources = sourceSort.key ? sortRows(sourceRows, SOURCE_SORT[sourceSort.key], sourceSort.dir) : sourceRows;
  const runs = logSummary?.runs ?? [];
  const sortedRuns = runSort.key ? sortRows(runs, RUN_SORT[runSort.key], runSort.dir) : runs;

  const alerts: { tone: Tone; label: string; title: string; detail: string }[] = [];
  for (const s of logSummary?.stalled ?? []) {
    const src = SOURCES.find((x) => x.key === s.model);
    alerts.push({ tone: 'err', label: `${src?.name ?? s.model} is falling behind`, title: `${src?.name ?? s.model} has not finished a run in 24 h`, detail: `${s.unfinished} runs were replaced by the next run before the container finished them.` });
  }
  const pub = logSummary?.publishes;
  if (pub?.blocked) {
    const codes = Object.entries(pub.byStatus).filter(([code]) => !code.startsWith('2')).map(([code, n]) => `${code} ×${n}`).join(', ');
    alerts.push({
      tone: 'err',
      label: 'Catalog updates refused',
      title: `The CDN refused ${pub.refused} catalog updates (${codes})`,
      detail: `Finished runs are uploaded to R2 but not live: vortex-cdn rejects PUT /meta/weather/current. ${pub.lastAcceptedTs ? `Last accepted ${local(pub.lastAcceptedTs)}.` : 'None accepted in the last 24 h.'} A 401 means vortex-cdn's WEATHER_PUBLISH_SECRET does not match vortex-weather's CDN_PUBLISH_SECRET.`,
    });
  }
  if (logSummary?.deploys.length) {
    alerts.push({ tone: 'warn', label: 'Deploys restarted the ingest', title: `${logSummary.deploys.length} deploy${logSummary.deploys.length > 1 ? 's' : ''} restarted the ingest container`, detail: `At ${logSummary.deploys.map(local).join(', ')}. A deploy ends the job that is running.` });
  }
  if (data && !data.catalog.ok) alerts.push({ tone: 'err', label: 'Catalog unreachable', title: 'The CDN catalog did not answer', detail: data.catalog.error });

  const newest = catalogModels.map((m) => (m.publishedAt ? Date.parse(m.publishedAt) : 0)).sort((a, b) => b - a)[0] || null;
  const errorTotal = logSummary?.errors.reduce((n, e) => n + e.count, 0) ?? null;

  return (
    <div className="space-y-6 text-slate-300">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <p className="text-[13px] text-slate-400">Admin / Platform / Weather</p>
          <h1 className="text-[28px] font-extrabold text-white tracking-tight">Vortex Weather</h1>
          <p className="text-sm text-slate-400">From the CDN catalog, an R2 listing and the weather worker's own log. Refreshes every minute.</p>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-[13px] text-slate-400 text-right">{data ? `Updated ${local(data.generatedAt)}` : 'Loading…'}</p>
          <a href="https://cdn.vortexnav.com/meta/weather/current" target="_blank" rel="noreferrer" className="min-h-[44px] inline-flex items-center px-4 rounded-lg border border-slate-600 text-sm font-semibold text-slate-200 hover:bg-slate-800">Catalog JSON</a>
          <button type="button" onClick={() => void load()} disabled={loading} className="min-h-[44px] inline-flex items-center gap-2 px-4 rounded-lg border border-slate-600 text-sm font-semibold text-slate-200 hover:bg-slate-800 disabled:opacity-60">
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" /> Refresh
          </button>
        </div>
      </header>

      {loadError && <p role="status" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{loadError}</p>}

      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((a) => (
            <div key={a.title} role="alert" aria-label={a.label} className={`rounded-lg border px-4 py-3 ${TONE[a.tone]}`}>
              <p className="text-sm font-semibold text-slate-100">{a.title}</p>
              <p className="text-[13px] text-slate-300 mt-0.5">{a.detail}</p>
            </div>
          ))}
        </div>
      )}

      {logs && !logs.configured && (
        <p className="rounded-lg border border-slate-600 bg-slate-800/60 px-4 py-3 text-sm text-slate-300">Processing view is off: {logs.message}</p>
      )}
      {logs && logs.configured && 'error' in logs && logs.error && (
        <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">{logs.error}. The catalog below is still current.</p>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Sources in the catalog', value: `${catalogModels.length}`, unit: `of ${SOURCES.length}` },
          { label: 'Newest publish', value: hoursAgo(newest, now), unit: 'ago' },
          { label: 'Cron kicks (24 h)', value: logSummary ? String(logSummary.kicks) : '—', unit: '' },
          { label: 'Longest busy stretch', value: logSummary ? String(logSummary.maxBusyMin) : '—', unit: 'min' },
          { label: 'Worker errors (24 h)', value: errorTotal === null ? '—' : String(errorTotal), unit: '' },
        ].map((k) => (
          <div key={k.label} className="rounded-xl border border-slate-700 bg-slate-800/50 px-5 py-4">
            <p className="text-[13px] text-slate-400">{k.label}</p>
            <p className="mt-1 flex items-baseline gap-2"><span className="text-[28px] font-extrabold text-white">{k.value}</span><span className="text-sm font-semibold text-slate-400">{k.unit}</span></p>
          </div>
        ))}
      </div>

      <Card title="Sources" caption="Status: catalog + the last cron kick for each model">
        <div className="overflow-x-auto">
          <table aria-label="Sources" className="w-full text-sm">
            <thead className="bg-slate-900/40 text-[13px] text-slate-400">
              <tr>{SOURCE_COLUMNS.map(([k, label]) => <SortHeader key={k} label={label} active={sourceSort.key === k} dir={sourceSort.dir} onSort={() => sourceSort.toggle(k)} />)}</tr>
            </thead>
            <tbody>
              {sortedSources.map((s) => (
                <tr key={s.key} className="border-t border-slate-700/70 align-top">
                  <td className="px-5 py-3"><p className="font-semibold text-slate-100">{s.name}</p><p className="font-mono text-[13px] text-slate-400">{s.catalogId} · {s.horizon}</p></td>
                  <td className="px-5 py-3" data-testid={`source-status-${s.key}`}><Pill tone={s.status[0]}>{s.status[1]}</Pill></td>
                  <td className="px-5 py-3 font-mono text-[13px] text-slate-200" data-testid={`source-run-${s.key}`}>{s.cat?.runId ?? '—'}</td>
                  <td className="px-5 py-3 text-[13px]">{s.cat?.publishedAt ? `${local(Date.parse(s.cat.publishedAt))} (${hoursAgo(Date.parse(s.cat.publishedAt), now)} ago)` : '—'}</td>
                  <td className="px-5 py-3 text-[13px]">{s.cat ? `${s.cat.hours.length} · +${s.cat.hours[0]}…+${s.cat.hours[s.cat.hours.length - 1]} h` : '—'}</td>
                  <td className="px-5 py-3 font-mono text-[13px]">{s.cat?.regions.join(' ') || '—'}</td>
                  <td className="px-5 py-3"><div className="flex flex-wrap gap-1.5">{s.cat?.products.map((p) => <span key={p} className="px-2 py-0.5 rounded-md border border-slate-600 font-mono text-[13px] text-slate-200">{p}</span>) ?? '—'}</div></td>
                  <td className="px-5 py-3 text-[13px]">{s.last ? <><span className="font-mono">{s.last.runId}</span> · {s.last.status}<br /><span className="text-slate-400">{local(s.last.ts)}</span></> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {logSummary && (
        <Card title="Runs, from every cron kick" caption={`Workers Logs · ingest_kick · last 24 h · ${logSummary.kicks} kicks`}>
          <div className="overflow-x-auto">
            <table aria-label="Runs" className="w-full text-sm">
              <thead className="bg-slate-900/40 text-[13px] text-slate-400">
                <tr>{RUN_COLUMNS.map(([k, label]) => <SortHeader key={k} label={label} active={runSort.key === k} dir={runSort.dir} onSort={() => runSort.toggle(k)} />)}</tr>
              </thead>
              <tbody>
                {sortedRuns.map((r) => {
                  const order = STATUS_ORDER.filter((k) => r.byStatus[k]).concat(Object.keys(r.byStatus).filter((k) => !STATUS_ORDER.includes(k)));
                  const [tone, label] = OUTCOME[r.outcome];
                  const time = ingestTime(r.attempts, data?.generatedAt ?? Date.now());
                  return (
                    <tr key={`${r.model}-${r.runId}`} className="border-t border-slate-700/70">
                      <td className="px-5 py-3 font-semibold text-slate-100 uppercase">{r.model}</td>
                      <td className="px-5 py-3 font-mono text-[13px] text-slate-200">{r.runId}{runIdToMs(r.runId) !== null && <span className="block font-sans text-slate-400">{local(runIdToMs(r.runId)!)}</span>}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex h-3 w-40 overflow-hidden rounded bg-slate-900" aria-hidden="true">
                            {order.map((k) => <span key={k} className={STATUS_BAR[k] ?? 'bg-slate-600'} style={{ width: `${(100 * r.byStatus[k]) / r.kicks}%` }} />)}
                          </div>
                          <span className="font-mono text-[13px] text-slate-300">{order.map((k) => `${k} ×${r.byStatus[k]}`).join(' · ')}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 font-mono text-[13px] text-slate-400">{local(r.firstTs)} → {local(r.lastTs)}</td>
                      <td className="px-5 py-3 text-[13px]">{time ? <><span className="font-mono text-slate-200">{time.text}</span><span className="block text-slate-400">{time.detail}</span></> : <span className="text-slate-500">—</span>}</td>
                      <td className="px-5 py-3"><Pill tone={tone}>{label}{r.starts > 1 ? ` · ${r.starts} starts` : ''}</Pill></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <WeatherTilesPanel data={tiles} error={tilesError} />

      <div className="grid lg:grid-cols-2 gap-6">
        <AvailabilityPanel models={catalogModels} />
        <TriggerPanel />
      </div>

      {logSummary && logSummary.errors.length > 0 && (
        <Card title="Worker errors" caption="Workers Logs · last 24 h">
          <ul className="divide-y divide-slate-700/70">
            {logSummary.errors.map((e) => (
              <li key={e.error} className="px-5 py-3 flex justify-between gap-4 text-sm"><span className="font-mono text-[13px] text-slate-300">{e.error}</span><span className="font-semibold text-slate-100">×{e.count}</span></li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function AvailabilityPanel({ models }: { models: WeatherCatalogModel[] }) {
  const [modelId, setModelId] = useState('');
  const [region, setRegion] = useState('');
  const [result, setResult] = useState<WeatherAvailabilityResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const model = models.find((m) => m.id === modelId) ?? models[0];
  const regions = model?.regions ?? [];
  const reg = regions.includes(region) ? region : regions[0] ?? '';

  const run = async () => {
    if (!model || !reg) return;
    setBusy(true); setError(null);
    try {
      setResult(await api.adminWeatherAvailability({ model: model.id, run: model.runId, region: reg }));
    } catch {
      setError('Could not list the CDN bucket.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="What is on the CDN" caption="One R2 listing, compared with the catalog">
      <div className="p-5 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-[13px] text-slate-400">Source
            <select value={model?.id ?? ''} onChange={(e) => { setModelId(e.target.value); setResult(null); }} className="min-h-[44px] rounded-lg border border-slate-600 bg-slate-900 px-3 text-sm text-slate-100">
              {models.map((m) => <option key={m.id} value={m.id}>{m.id} · {m.runId}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[13px] text-slate-400">Region
            <select value={reg} onChange={(e) => { setRegion(e.target.value); setResult(null); }} className="min-h-[44px] rounded-lg border border-slate-600 bg-slate-900 px-3 text-sm text-slate-100">
              {regions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <button type="button" onClick={() => void run()} disabled={!model || busy} className="min-h-[44px] px-4 rounded-lg bg-cyan-700 text-sm font-semibold text-white hover:bg-cyan-600 disabled:opacity-60">{busy ? 'Listing…' : 'List objects'}</button>
        </div>
        {error && <p className="text-sm text-red-300">{error}</p>}
        {result?.availability && (
          <div className="space-y-2">
            <p className="text-sm text-slate-200"><span className="font-semibold">{result.availability.present}</span> of {result.availability.expected} objects present ({result.keys} keys listed).</p>
            <ul className="space-y-1.5">
              {result.availability.products.map((p) => (
                <li key={p.product} className="text-[13px]">
                  <span className="font-mono text-slate-100">{p.product}</span>{' '}
                  <span className={p.missingHours.length ? 'text-amber-300' : 'text-emerald-300'}>{p.missingHours.length ? `missing +${p.missingHours.join(', +')} h` : 'complete'}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {result && !result.availability && <p className="text-sm text-slate-400">That run is not in the catalog any more; {result.keys} keys are still listed under it.</p>}
      </div>
    </Card>
  );
}

function TriggerPanel() {
  const [model, setModel] = useState<SourceKey>('icon');
  const [mode, setMode] = useState<'auto' | 'refetch' | 'republish'>('auto');
  const [date, setDate] = useState('');
  const [cycle, setCycle] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<{ tone: Tone; text: string } | null>(null);
  const cycles = SOURCES.find((s) => s.key === model)!.cycles;

  const params: WeatherTriggerParams = { model, mode, ...(date && cycle ? { date, cycle } : {}) };
  const canSend = (!date && !cycle) || (/^\d{8}$/.test(date) && (cycles as readonly string[]).includes(cycle));

  const send = async () => {
    setConfirming(false);
    try {
      const r = await api.adminWeatherIngest(params);
      const status = (r.upstream as { status?: string } | null)?.status ?? 'accepted';
      setResult({ tone: 'ok', text: `The weather worker answered: ${status}. Progress shows in the runs table within a few minutes.` });
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { error?: string; message?: string } } };
      const msg = e.response?.data?.message ?? e.response?.data?.error ?? 'The trigger failed.';
      setResult({ tone: 'err', text: msg });
    }
  };

  return (
    <Card title="Run an ingest now" caption="Each model has its own container; a weather deploy ends the jobs running">
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-[13px] text-slate-400">Model
            <select value={model} onChange={(e) => { setModel(e.target.value as SourceKey); setCycle(''); setConfirming(false); }} className="min-h-[44px] rounded-lg border border-slate-600 bg-slate-900 px-3 text-sm text-slate-100">
              {SOURCES.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[13px] text-slate-400">Mode
            <select value={mode} onChange={(e) => { setMode(e.target.value as typeof mode); setConfirming(false); }} className="min-h-[44px] rounded-lg border border-slate-600 bg-slate-900 px-3 text-sm text-slate-100">
              <option value="auto">auto: newest run if not done</option>
              <option value="refetch">refetch: download and process again</option>
              <option value="republish">republish: re-encode raw data held</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[13px] text-slate-400">Run date
            <input value={date} onChange={(e) => { setDate(e.target.value.trim()); setConfirming(false); }} placeholder="YYYYMMDD (blank = latest)" inputMode="numeric" className="min-h-[44px] rounded-lg border border-slate-600 bg-slate-900 px-3 font-mono text-sm text-slate-100 placeholder:text-slate-500" />
          </label>
          <label className="flex flex-col gap-1 text-[13px] text-slate-400">Run hour
            <select value={cycle} onChange={(e) => { setCycle(e.target.value); setConfirming(false); }} className="min-h-[44px] rounded-lg border border-slate-600 bg-slate-900 px-3 text-sm text-slate-100">
              <option value="">latest</option>
              {cycles.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
        </div>
        {!confirming ? (
          <button type="button" disabled={!canSend} onClick={() => { setResult(null); setConfirming(true); }} className="min-h-[44px] px-4 rounded-lg bg-cyan-700 text-sm font-semibold text-white hover:bg-cyan-600 disabled:opacity-60">Run ingest…</button>
        ) : (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 space-y-3">
            <p className="text-sm text-amber-100">Trigger <span className="font-mono">{params.model}</span> in <span className="font-mono">{params.mode}</span> mode for {params.date ? <span className="font-mono">{params.date} {params.cycle}Z</span> : 'the newest run'}? It joins the queue behind any running job.</p>
            <div className="flex gap-2">
              <button type="button" onClick={() => void send()} className="min-h-[44px] px-4 rounded-lg bg-amber-600 text-sm font-semibold text-white hover:bg-amber-500">Yes, trigger</button>
              <button type="button" onClick={() => setConfirming(false)} className="min-h-[44px] px-4 rounded-lg border border-slate-600 text-sm font-semibold text-slate-200 hover:bg-slate-800">Cancel</button>
            </div>
          </div>
        )}
        {!canSend && <p className="text-[13px] text-amber-300">Give a date as YYYYMMDD and a run hour this model has, or leave both blank.</p>}
        {result && <p role="status" className={`rounded-lg border px-4 py-3 text-sm ${TONE[result.tone]}`}>{result.text}</p>}
      </div>
    </Card>
  );
}
